import { Link, useNavigate } from "react-router-dom";
import { Feather, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Back goes to the previous in-app page, or home when this page was opened
// directly — from a link, or by a reviewer pasting the URL — where nav(-1)
// would do nothing or leave the site. React Router records the history index.
const goBack = (nav) => (window.history.state?.idx > 0 ? nav(-1) : nav("/"));

// Public, signed-out pages: /privacy, /terms, /delete-account.
// Google Play requires the privacy policy and the account-deletion route to be
// reachable at public URLs, without signing in or installing the app.

export const SUPPORT_EMAIL = "HELPDESK@htokoki.com";
export const EFFECTIVE_DATE = "October 2, 2026";

export const LEGAL_LINKS = [
    { to: "/privacy", label: "Privacy" },
    { to: "/terms", label: "Terms" },
    { to: "/delete-account", label: "Delete account" },
    { to: "/faq", label: "FAQ" },
];

export function Section({ title, children }) {
    return (
        <section className="mt-10">
            <h2 className="font-heading text-2xl font-light tracking-tight mb-3">{title}</h2>
            <div className="space-y-3 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
        </section>
    );
}

export function Mail() {
    return (
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary underline underline-offset-2">
            {SUPPORT_EMAIL}
        </a>
    );
}

export default function LegalLayout({ eyebrow, title, intro, testId, children }) {
    const nav = useNavigate();
    return (
        <div className="min-h-screen bg-background text-foreground" data-testid={testId}>
            <header className="px-6 lg:px-12 pt-8 pb-2 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80">
                    <Feather className="w-5 h-5 text-primary" />
                    <span className="font-heading text-2xl tracking-tight">Day Dreamer</span>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => goBack(nav)}>
                    <ArrowLeft className="w-4 h-4" /> Back
                </Button>
            </header>

            <main className="px-6 lg:px-12 pt-10 pb-16 max-w-3xl mx-auto">
                <div className="ui-label mb-4">{eyebrow}</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-[1.1]">{title}</h1>
                <p className="mt-4 text-sm text-muted-foreground">Effective {EFFECTIVE_DATE}</p>
                {intro && <p className="mt-6 text-[15px] leading-relaxed text-muted-foreground">{intro}</p>}
                {children}
            </main>

            <footer className="px-6 lg:px-12 pb-12 max-w-3xl mx-auto border-t border-border/60 pt-6">
                <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                    {LEGAL_LINKS.map((l) => (
                        <Link key={l.to} to={l.to} className="hover:text-foreground">{l.label}</Link>
                    ))}
                    <Mail />
                </nav>
                <p className="mt-4 text-xs text-muted-foreground">Day Dreamer is operated by H. Tokoki LLC, South Carolina, USA.</p>
            </footer>
        </div>
    );
}
